import React, { useState, useEffect } from 'react';
import { useTranslation } from 'utils/i18n';
import { getApiEndpoint } from 'utils/centralizedConfig';
import './ServiceLevelAgreement.css';
import LatencySparkline from './LatencySparkline';

// Import extracted logic
import translations from './translations';

/**
 * ServiceLevelAgreement Component
 * Displays SLA information and metrics with real-time data
 */
const ServiceLevelAgreement = () => {
  const { language } = useTranslation();
  
  // State for SLA data
  const [slaData, setSlaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timePeriod, setTimePeriod] = useState('day'); // hour, day, month, year - default to last day
  const [regionFilter, setRegionFilter] = useState('germanywestcentral'); // Azure region filter - default to Germany West Central
  const [expandedGroups, setExpandedGroups] = useState(new Set()); // Track expanded groups (empty = all collapsed by default)
  
  // Common Azure regions
  const azureRegions = [
    { value: 'germanywestcentral', label: 'Germany West Central', enabled: true },
    { value: 'germanynorth', label: 'Germany North', enabled: false },
    { value: 'westeurope', label: 'West Europe', enabled: false },
    { value: 'northeurope', label: 'North Europe', enabled: false },
    { value: 'eastus', label: 'East US', enabled: false },
    { value: 'eastus2', label: 'East US 2', enabled: false },
    { value: 'westus', label: 'West US', enabled: false },
    { value: 'westus2', label: 'West US 2', enabled: false },
    { value: 'westus3', label: 'West US 3', enabled: false },
    { value: 'centralus', label: 'Central US', enabled: false },
    { value: 'southcentralus', label: 'South Central US', enabled: false },
    { value: 'northcentralus', label: 'North Central US', enabled: false },
    { value: 'canadacentral', label: 'Canada Central', enabled: false },
    { value: 'canadaeast', label: 'Canada East', enabled: false },
    { value: 'uksouth', label: 'UK South', enabled: false },
    { value: 'ukwest', label: 'UK West', enabled: false },
    { value: 'francecentral', label: 'France Central', enabled: false },
    { value: 'francesouth', label: 'France South', enabled: false },
    { value: 'switzerlandnorth', label: 'Switzerland North', enabled: false },
    { value: 'switzerlandwest', label: 'Switzerland West', enabled: false },
    { value: 'italynorth', label: 'Italy North', enabled: false },
    { value: 'norwayeast', label: 'Norway East', enabled: false },
    { value: 'norwaywest', label: 'Norway West', enabled: false },
    { value: 'swedencentral', label: 'Sweden Central', enabled: false },
    { value: 'polandcentral', label: 'Poland Central', enabled: false },
    { value: 'southeastasia', label: 'Southeast Asia', enabled: false },
    { value: 'eastasia', label: 'East Asia', enabled: false },
    { value: 'japaneast', label: 'Japan East', enabled: false },
    { value: 'japanwest', label: 'Japan West', enabled: false },
    { value: 'koreacentral', label: 'Korea Central', enabled: false },
    { value: 'koreasouth', label: 'Korea South', enabled: false },
    { value: 'australiaeast', label: 'Australia East', enabled: false },
    { value: 'australiasoutheast', label: 'Australia Southeast', enabled: false },
    { value: 'australiacentral', label: 'Australia Central', enabled: false },
    { value: 'australiacentral2', label: 'Australia Central 2', enabled: false },
    { value: 'brazilsouth', label: 'Brazil South', enabled: false },
    { value: 'southafricanorth', label: 'South Africa North', enabled: false },
    { value: 'southafricawest', label: 'South Africa West', enabled: false },
    { value: 'uaenorth', label: 'UAE North', enabled: false },
    { value: 'uaecentral', label: 'UAE Central', enabled: false },
    { value: 'indiawest', label: 'India West', enabled: false },
    { value: 'indiacentral', label: 'India Central', enabled: false },
    { value: 'indiasouth', label: 'India South', enabled: false },
  ];

  // Get translations
  const t = (key, defaultValue = '') => {
    const pageTranslations = translations[language] || translations.en;
    const value = pageTranslations[key];
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
    return defaultValue !== '' ? defaultValue : key;
  };

  // Fetch SLA data from public endpoint
  useEffect(() => {
    const fetchSlaData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Use public API endpoint (no authentication required)
        const baseUrl = await getApiEndpoint('public');
        const isLocalhost = baseUrl.includes('localhost') || baseUrl.includes('127.0.0.1');
        const apiBase = isLocalhost ? `${baseUrl}/api` : baseUrl;
        // Avoid double /api/api/ when apiBase already ends with /api
        const hasApiPrefix = apiBase && (apiBase.endsWith('/api') || apiBase.includes('/api/'));
        const path = hasApiPrefix ? '/v1/sla' : '/api/v1/sla';
        
        // Build query parameters
        const params = new URLSearchParams({
          period: timePeriod
        });
        
        // Region filter will be implemented later - for now everything is germanywestcentral
        // if (regionFilter) {
        //   params.append('region', regionFilter);
        // }
        
        const url = `${apiBase}${path}?${params.toString()}`;
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        const data = await response.json();

        if (!response.ok) {
          setError(data.error || data.message || `HTTP ${response.status}`);
          return;
        }

        if (data.success) {
          setSlaData(data);
        } else {
          setError(data.error || 'Failed to fetch SLA data');
        }
      } catch (err) {
        console.error('Error fetching SLA data:', err);
        setError(err.message || 'Failed to fetch SLA data');
      } finally {
        setLoading(false);
      }
    };

    fetchSlaData();
  }, [timePeriod, regionFilter]);


  // Parse endpoint path into hierarchical structure
  const parseEndpointPath = React.useCallback((endpoint) => {
    // Handle legacy action-based endpoint names (from old SLA aggregation format)
    // These should be mapped to their proper full path format
    const legacyActionMap = {
      'create-session': 'POST /api/v1/chatbot/sessions',
      'generate-response': 'POST /api/v1/chatbot/generate',
      'generate-by-session': 'POST /api/v1/chatbot/sessions/{session_id}/generate',
      'create_session': 'POST /api/v1/chatbot/sessions',
      'generate_response': 'POST /api/v1/chatbot/generate'
    };
    
    // Check if this is a legacy action-based endpoint (no /api/ path)
    let normalizedEndpoint = endpoint;
    if (!endpoint.includes('/api/')) {
      // Try to extract action name (might be just action, or "METHOD action")
      const parts = endpoint.split(' ');
      const actionName = parts.length > 1 ? parts[1] : parts[0];
      
      if (legacyActionMap[actionName]) {
        normalizedEndpoint = legacyActionMap[actionName];
      }
    }
    
    // "GET /api/v1/sdk/bots/demo/bot_demo"
    const parts = normalizedEndpoint.split(' ');
    const method = parts[0];
    const path = parts[1] || '';
    const segments = path.split('/').filter(s => s);
    
    // segments: ['api', 'public', 'sdk', 'bots', 'demo', 'bot_demo']
    const apiType = segments[1] || 'unknown'; // 'public', 'v1', 'customer'
    
    // Structure: Root (API type) -> Sub (Category) -> Sub-sub (Subcategory) -> Endpoint
    // segments[1] = API type (public, v1, customer)
    // segments[2] = Category (sub)
    // segments[3] = Subcategory (sub-sub)
    // segments[4+] = Endpoint name
    
    let category = null;
    let subcategory = null;
    let endpointName = null;
    
    if (segments.length === 3) {
      // /api/v1/tenants - segments[2] is the category, endpoint is the category itself
      category = segments[2];
      endpointName = segments[2]; // Endpoint name is same as category
    } else if (segments.length === 4) {
      // /api/v1/users/profile - segments[2] is category, segments[3] is subcategory
      category = segments[2];
      subcategory = segments[3];
      endpointName = segments[3]; // Endpoint name is same as subcategory
    } else if (segments.length >= 5) {
      // /api/v1/sdk/bots/demo - segments[2] is category, segments[3] is subcategory, rest is endpoint
      category = segments[2];
      subcategory = segments[3];
      endpointName = segments.slice(4).join('/') || segments[3];
    } else {
      // Fallback
      category = segments[2] || null;
      subcategory = segments[3] || null;
      endpointName = segments.slice(4).join('/') || segments[3] || segments[2] || 'root';
    }
    
    return {
      method,
      apiType,
      category,
      subcategory,
      endpointName,
      fullPath: path
    };
  }, []);

  // Group endpoints hierarchically
  const groupedEndpoints = React.useMemo(() => {
    if (!slaData?.endpoints) return {};
    
    const groups = {};
    
    Object.entries(slaData.endpoints).forEach(([endpoint, stats]) => {
      const parsed = parseEndpointPath(endpoint);
      
      // Build hierarchical structure
      if (!groups[parsed.apiType]) {
        groups[parsed.apiType] = {};
      }
      
      // All endpoints should have a category (segments[2])
      // Root level = API type only (public, v1, customer)
      // Sub level = Category (tenants, users, sdk, bot-operations, etc.)
      // Sub-sub level = Subcategory (profile, bots, etc.)
      
      if (!parsed.category) {
        // This shouldn't happen with proper API structure, but handle gracefully
        if (!groups[parsed.apiType]._root) {
          groups[parsed.apiType]._root = [];
        }
        groups[parsed.apiType]._root.push({
          endpoint,
          stats,
          parsed,
          displayName: parsed.endpointName || parsed.apiType
        });
        return; // Skip to next endpoint
      }
      
      // Ensure category exists
      if (!groups[parsed.apiType][parsed.category]) {
        groups[parsed.apiType][parsed.category] = {};
      }
      
      if (parsed.subcategory) {
        // Has subcategory: /api/v1/users/profile
        if (!groups[parsed.apiType][parsed.category][parsed.subcategory]) {
          groups[parsed.apiType][parsed.category][parsed.subcategory] = [];
        }
        groups[parsed.apiType][parsed.category][parsed.subcategory].push({
          endpoint,
          stats,
          parsed,
          displayName: parsed.endpointName
        });
      } else {
        // No subcategory: /api/v1/tenants
        // Endpoint is at category level
        if (!groups[parsed.apiType][parsed.category]._endpoints) {
          groups[parsed.apiType][parsed.category]._endpoints = [];
        }
        groups[parsed.apiType][parsed.category]._endpoints.push({
          endpoint,
          stats,
          parsed,
          displayName: parsed.endpointName || parsed.category
        });
      }
    });
    
    // Sort endpoints within groups by latency (descending)
    const sortEndpoints = (endpoints) => {
      return endpoints.sort((a, b) => {
        const avgA = a.stats.average_duration_ms || 0;
        const avgB = b.stats.average_duration_ms || 0;
        return avgB - avgA;
      });
    };
    
    // Sort all nested endpoints
    Object.keys(groups).forEach(apiType => {
      if (groups[apiType]._endpoints) {
        groups[apiType]._endpoints = sortEndpoints(groups[apiType]._endpoints);
      }
      Object.keys(groups[apiType]).forEach(category => {
        if (category === '_endpoints') return;
        if (groups[apiType][category]._endpoints) {
          groups[apiType][category]._endpoints = sortEndpoints(groups[apiType][category]._endpoints);
        }
        Object.keys(groups[apiType][category]).forEach(subcategory => {
          if (subcategory === '_endpoints') return;
          if (Array.isArray(groups[apiType][category][subcategory])) {
            groups[apiType][category][subcategory] = sortEndpoints(groups[apiType][category][subcategory]);
          }
        });
      });
    });
    
    return groups;
  }, [slaData, parseEndpointPath]);

  // Get period text for latency trend
  const getPeriodText = () => {
    switch (timePeriod) {
      case 'hour':
        return 'last hour';
      case 'day':
        return 'last 24 hours';
      case 'week':
        return 'last 7 days';
      case 'month':
        return 'last 30 days';
      case 'year':
        return 'last 365 days';
      default:
        return 'selected period';
    }
  };

  const formatMs = (v) => {
    if (v == null || Number.isNaN(Number(v))) return null;
    return `${Number(v).toFixed(2)} ms`;
  };

  // Aggregate latency trends from multiple endpoints by hour
  // For each hour, averages the latency values across all endpoints in the group
  const aggregateLatencyTrends = React.useCallback((endpoints) => {
    if (!endpoints || endpoints.length === 0) {
      return [];
    }
    
    // Collect all latency trend data points from all endpoints
    const allTrends = endpoints
      .map(e => e.stats?.latency_trend || [])
      .filter(trend => Array.isArray(trend) && trend.length > 0);
    
    if (allTrends.length === 0) {
      return [];
    }
    
    // Group by hour and collect latency values for averaging
    const hourMap = new Map();
    
    allTrends.forEach(trend => {
      trend.forEach(point => {
        const bucketKey = point.ts || point.hour;
        if (bucketKey && point.avg_ms != null && !isNaN(point.avg_ms)) {
          if (!hourMap.has(bucketKey)) {
            hourMap.set(bucketKey, { sum: 0, count: 0 });
          }
          const entry = hourMap.get(bucketKey);
          entry.sum += point.avg_ms;
          entry.count += 1;
        }
      });
    });
    
    // Convert to array and calculate average latency for each hour
    // Average = sum of all latency values / number of endpoints with data
    const aggregatedTrend = Array.from(hourMap.entries())
      .map(([bucketKey, bucket]) => ({
        hour: bucketKey,
        ts: bucketKey,
        avg_ms: bucket.sum / bucket.count
      }))
      .sort((a, b) => String(a.hour).localeCompare(String(b.hour)));
    
    return aggregatedTrend;
  }, []);

  // Calculate aggregate stats for a group of endpoints
  const calculateGroupStats = React.useCallback((endpoints) => {
    if (!endpoints || endpoints.length === 0) {
      return { avgLatency: null, count: 0, latencyTrend: [] };
    }
    
    const latencies = endpoints
      .map(e => e.stats?.average_duration_ms)
      .filter(l => l != null && !isNaN(l));
    
    const avgLatency = latencies.length > 0
      ? latencies.reduce((sum, l) => sum + l, 0) / latencies.length
      : null;
    
    // Aggregate latency trends
    const latencyTrend = aggregateLatencyTrends(endpoints);
    
    return { 
      avgLatency, 
      count: endpoints.length,
      latencyTrend
    };
  }, [aggregateLatencyTrends]);

  // Collect all endpoints from a group structure (recursive)
  const collectAllEndpoints = React.useCallback((obj) => {
    const endpoints = [];
    
    if (Array.isArray(obj)) {
      endpoints.push(...obj);
    } else if (obj && typeof obj === 'object') {
      if (obj._endpoints) {
        endpoints.push(...obj._endpoints);
      }
      Object.values(obj).forEach(v => {
        if (v !== obj._endpoints && v !== null && typeof v === 'object') {
          endpoints.push(...collectAllEndpoints(v));
        }
      });
    }
    
    return endpoints;
  }, []);

  // Toggle group expand/collapse
  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey); // Collapse
      } else {
        next.add(groupKey); // Expand
      }
      return next;
    });
  };

  // Map API type to display name
  const getApiTypeDisplayName = (apiType) => {
    const displayNames = {
      'public': 'SDKs',
      'v1': 'Portal',
      'customer': 'Customer'
    };
    return displayNames[apiType] || apiType.charAt(0).toUpperCase() + apiType.slice(1);
  };

  return (
    <div className="sla-page">
      <section className="sla-hero">
        <div className="sla-hero-content">
          <h1 className="sla-hero-title">{t('title', 'Service Level Agreement')}</h1>
          <p className="sla-hero-subtitle">
            {t('subtitle', 'Real-time performance metrics and service level statistics')}
          </p>
        </div>
      </section>

      <section className="sla-overview">
        <div className="sla-container">
          <div className="sla-section-header">
            <h2 className="sla-section-title">{t('overview.heading', 'SLA Overview')}</h2>
            <p className="sla-section-subtitle">
              {t('overview.subtitle', 'Transparent service levels and performance guarantees for all B-esper customers')}
            </p>
          </div>
          
          {/* Filters */}
          <div className="sla-filters">
            <div className="sla-filter-group">
              <label htmlFor="time-period" className="sla-filter-label">
                {t('filters.timePeriod', 'Time Period')}
              </label>
              <select
                id="time-period"
                className="sla-filter-select"
                value={timePeriod}
                onChange={(e) => setTimePeriod(e.target.value)}
              >
                <option value="hour">{t('filters.hour', 'Last Hour')}</option>
                <option value="day">{t('filters.day', 'Last Day')}</option>
                <option value="week">{t('filters.week', 'Last Week')}</option>
                <option value="month">{t('filters.month', 'Last Month')}</option>
                <option value="year">{t('filters.year', 'Last Year')}</option>
              </select>
            </div>
            
            <div className="sla-filter-group">
              <label htmlFor="region-filter" className="sla-filter-label">
                {t('filters.region', 'Region')}
              </label>
              <select
                id="region-filter"
                className="sla-filter-select sla-filter-select-wide"
                value={regionFilter}
                onChange={(e) => setRegionFilter(e.target.value)}
                disabled={false} // Will be disabled when filter is not functional
              >
                {azureRegions.map(region => (
                  <option 
                    key={region.value} 
                    value={region.value}
                    disabled={!region.enabled}
                  >
                    {region.label}
                  </option>
                ))}
              </select>
            </div>
            
          </div>

          {/* Loading State */}
          {loading && (
            <div className="sla-loading">
              <div className="sla-loading-spinner"></div>
              <p>{t('loading', 'Loading SLA data...')}</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="sla-error">
              <p>{t('error', 'Error')}: {error}</p>
            </div>
          )}

          {/* SLA Data Display */}
          {!loading && !error && slaData && (
            <div className="sla-data-display">
              {Object.keys(groupedEndpoints).length === 0 ? (
                <div className="sla-empty-state">
                  <p>{t('noData', 'No SLA data available for the selected period')}</p>
                </div>
              ) : (
                <div className="sla-endpoints-table">
                  <div className="sla-table-header">
                    <h3>{t('table.summary', 'Summary')}</h3>
                  </div>
                  <div className="sla-table-wrapper">
                    <table className="sla-table">
                      <thead>
                        <tr>
                          <th>{t('table.endpoint', 'Endpoint')}</th>
                          <th className="sla-table-number">{t('table.avgLatency', 'Avg / P95 (ms)')}</th>
                          <th className="sla-table-number">{t('table.latencyTrend', 'Latency Trend')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(groupedEndpoints).map(([apiType, categories]) => {
                          const apiTypeKey = `api-${apiType}`;
                          const isApiTypeExpanded = expandedGroups.has(apiTypeKey);
                          
                          // Collect all endpoints for this API type
                          const allApiEndpoints = collectAllEndpoints(categories);
                          const apiStats = calculateGroupStats(allApiEndpoints);
                          
                          return (
                            <React.Fragment key={apiType}>
                              {/* API Type Header */}
                              <tr className="sla-group-header sla-group-level-1">
                                <td>
                                  <button
                                    className="sla-group-toggle"
                                    onClick={() => toggleGroup(apiTypeKey)}
                                    aria-expanded={isApiTypeExpanded}
                                  >
                                    <span className="sla-group-icon">{isApiTypeExpanded ? '▼' : '▶'}</span>
                                    <span className="sla-group-name">{getApiTypeDisplayName(apiType)}</span>
                                    <span className="sla-group-count">({apiStats.count} {apiStats.count === 1 ? 'endpoint' : 'endpoints'})</span>
                                  </button>
                                </td>
                                {!isApiTypeExpanded ? (
                                  <>
                                    <td className="sla-table-number">
                                      {apiStats.avgLatency ? (
                                        <span className="sla-latency-value">{apiStats.avgLatency.toFixed(2)} ms</span>
                                      ) : (
                                        <span className="sla-no-data">{t('table.noData', 'N/A')}</span>
                                      )}
                                    </td>
                                    <td className="sla-table-number">
                                      <div className="sla-latency-trend-cell">
                                        <LatencySparkline data={apiStats.latencyTrend || []} width={120} height={30} />
                                        <span className="sla-period-text">latency over {getPeriodText()}</span>
                                      </div>
                                    </td>
                                  </>
                                ) : (
                                  <>
                                    <td></td>
                                    <td></td>
                                  </>
                                )}
                              </tr>
                              
                              {isApiTypeExpanded && Object.entries(categories).map(([category, subcategories]) => {
                                if (category === '_root') {
                                  // Root-level endpoints (no category, e.g., /api/v1/tenants)
                                  return subcategories.map(({ endpoint, stats }) => (
                                    <tr key={endpoint} className="sla-endpoint-row sla-endpoint-level-2">
                                      <td>
                                        <code className="sla-endpoint-code">{endpoint}</code>
                                        {stats.request_count != null ? (
                                          <div className="sla-endpoint-meta">{t('table.requests', 'Requests')}: {stats.request_count}</div>
                                        ) : null}
                                      </td>
                                      <td className="sla-table-number">
                                        {formatMs(stats.average_duration_ms) ? (
                                          <span className="sla-latency-value">
                                            {formatMs(stats.average_duration_ms)}
                                            {formatMs(stats.p95_duration_ms) ? (
                                              <span className="sla-latency-sub"> · P95 {formatMs(stats.p95_duration_ms)}</span>
                                            ) : null}
                                          </span>
                                        ) : (
                                          <span className="sla-no-data">{t('table.noData', 'N/A')}</span>
                                        )}
                                      </td>
                                      <td className="sla-table-number">
                                        <div className="sla-latency-trend-cell">
                                          <LatencySparkline data={stats.latency_trend || []} width={120} height={30} />
                                          <span className="sla-period-text">latency over {getPeriodText()}</span>
                                        </div>
                                      </td>
                                    </tr>
                                  ));
                                }
                                
                                const categoryKey = `${apiTypeKey}-${category}`;
                                const isCategoryExpanded = expandedGroups.has(categoryKey);
                                
                                // Collect all endpoints in this category
                                const allCategoryEndpoints = collectAllEndpoints(subcategories);
                                const categoryStats = calculateGroupStats(allCategoryEndpoints);
                                
                                return (
                                  <React.Fragment key={category}>
                                    {/* Category Header */}
                                    <tr className="sla-group-header sla-group-level-2">
                                      <td>
                                        <button
                                          className="sla-group-toggle"
                                          onClick={() => toggleGroup(categoryKey)}
                                          aria-expanded={isCategoryExpanded}
                                        >
                                          <span className="sla-group-icon">{isCategoryExpanded ? '▼' : '▶'}</span>
                                          <span className="sla-group-name">{category.charAt(0).toUpperCase() + category.slice(1)}</span>
                                          <span className="sla-group-count">({categoryStats.count} {categoryStats.count === 1 ? 'endpoint' : 'endpoints'})</span>
                                        </button>
                                      </td>
                                      {!isCategoryExpanded ? (
                                        <>
                                          <td className="sla-table-number">
                                            {categoryStats.avgLatency ? (
                                              <span className="sla-latency-value">{categoryStats.avgLatency.toFixed(2)} ms</span>
                                            ) : (
                                              <span className="sla-no-data">{t('table.noData', 'N/A')}</span>
                                            )}
                                          </td>
                                          <td className="sla-table-number">
                                            <div className="sla-latency-trend-cell">
                                              <LatencySparkline data={categoryStats.latencyTrend || []} width={120} height={30} />
                                              <span className="sla-period-text">latency over {getPeriodText()}</span>
                                            </div>
                                          </td>
                                        </>
                                      ) : (
                                        <>
                                          <td></td>
                                          <td></td>
                                        </>
                                      )}
                                    </tr>
                                    
                                    {isCategoryExpanded && (
                                      <>
                                        {/* Category-level endpoints */}
                                        {subcategories._endpoints && subcategories._endpoints.map(({ endpoint, stats }) => (
                                          <tr key={endpoint} className="sla-endpoint-row sla-endpoint-level-3">
                                            <td>
                                              <code className="sla-endpoint-code">{endpoint}</code>
                                              {stats.request_count != null ? (
                                                <div className="sla-endpoint-meta">{t('table.requests', 'Requests')}: {stats.request_count}</div>
                                              ) : null}
                                              {stats.error_rate != null && stats.error_rate > 0 ? (
                                                <div className="sla-endpoint-meta">{t('table.errorRate', 'Errors')}: {(stats.error_rate * 100).toFixed(2)}%</div>
                                              ) : null}
                                            </td>
                                            <td className="sla-table-number">
                                              {formatMs(stats.average_duration_ms) ? (
                                                <span className="sla-latency-value">
                                                  {formatMs(stats.average_duration_ms)}
                                                  {formatMs(stats.p95_duration_ms) ? (
                                                    <span className="sla-latency-sub"> · P95 {formatMs(stats.p95_duration_ms)}</span>
                                                  ) : null}
                                                </span>
                                              ) : (
                                                <span className="sla-no-data">{t('table.noData', 'N/A')}</span>
                                              )}
                                            </td>
                                            <td className="sla-table-number">
                                              <div className="sla-latency-trend-cell">
                                                <LatencySparkline data={stats.latency_trend || []} width={120} height={30} />
                                                <span className="sla-period-text">latency over {getPeriodText()}</span>
                                              </div>
                                            </td>
                                          </tr>
                                        ))}
                                        
                                        {/* Subcategory groups */}
                                        {Object.entries(subcategories).map(([subcategory, endpoints]) => {
                                          if (subcategory === '_endpoints') return null;
                                          
                                          const subcategoryKey = `${categoryKey}-${subcategory}`;
                                          const isSubcategoryExpanded = expandedGroups.has(subcategoryKey);
                                          const subcategoryStats = calculateGroupStats(endpoints);
                                          
                                          return (
                                            <React.Fragment key={subcategory}>
                                              {/* Subcategory Header */}
                                              <tr className="sla-group-header sla-group-level-3">
                                                <td>
                                                  <button
                                                    className="sla-group-toggle"
                                                    onClick={() => toggleGroup(subcategoryKey)}
                                                    aria-expanded={isSubcategoryExpanded}
                                                  >
                                                    <span className="sla-group-icon">{isSubcategoryExpanded ? '▼' : '▶'}</span>
                                                    <span className="sla-group-name">{subcategory.charAt(0).toUpperCase() + subcategory.slice(1)}</span>
                                                    <span className="sla-group-count">({subcategoryStats.count} {subcategoryStats.count === 1 ? 'endpoint' : 'endpoints'})</span>
                                                  </button>
                                                </td>
                                                {!isSubcategoryExpanded ? (
                                                  <>
                                                    <td className="sla-table-number">
                                                      {subcategoryStats.avgLatency ? (
                                                        <span className="sla-latency-value">{subcategoryStats.avgLatency.toFixed(2)} ms</span>
                                                      ) : (
                                                        <span className="sla-no-data">{t('table.noData', 'N/A')}</span>
                                                      )}
                                                    </td>
                                                    <td className="sla-table-number">
                                                      <div className="sla-latency-trend-cell">
                                                        <LatencySparkline data={subcategoryStats.latencyTrend || []} width={120} height={30} />
                                                        <span className="sla-period-text">latency over {getPeriodText()}</span>
                                                      </div>
                                                    </td>
                                                  </>
                                                ) : (
                                                  <>
                                                    <td></td>
                                                    <td></td>
                                                  </>
                                                )}
                                              </tr>
                                              
                                              {isSubcategoryExpanded && endpoints.map(({ endpoint, stats }) => (
                                                <tr key={endpoint} className="sla-endpoint-row sla-endpoint-level-4">
                                                  <td>
                                                    <code className="sla-endpoint-code">{endpoint}</code>
                                                    {stats.request_count != null ? (
                                                      <div className="sla-endpoint-meta">{t('table.requests', 'Requests')}: {stats.request_count}</div>
                                                    ) : null}
                                                  </td>
                                                  <td className="sla-table-number">
                                                    {formatMs(stats.average_duration_ms) ? (
                                                      <span className="sla-latency-value">
                                                        {formatMs(stats.average_duration_ms)}
                                                        {formatMs(stats.p95_duration_ms) ? (
                                                          <span className="sla-latency-sub"> · P95 {formatMs(stats.p95_duration_ms)}</span>
                                                        ) : null}
                                                      </span>
                                                    ) : (
                                                      <span className="sla-no-data">{t('table.noData', 'N/A')}</span>
                                                    )}
                                                  </td>
                                                  <td className="sla-table-number">
                                                    <div className="sla-latency-trend-cell">
                                                      <LatencySparkline data={stats.latency_trend || []} width={120} height={30} />
                                                      <span className="sla-period-text">latency over {getPeriodText()}</span>
                                                    </div>
                                                  </td>
                                                </tr>
                                              ))}
                                            </React.Fragment>
                                          );
                                        })}
                                      </>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      </section>
    </div>
  );
};

export default ServiceLevelAgreement;


